import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useSFX } from 'js/hooks/useSFX';

import defaultCardImage from '../../../images/static/card_default.jpg';

// import selectors
import { getTranslationById, getOnlineStatus } from 'js/store/app/App.selectors';

// import hooks
import { useRouteInfo } from 'js/hooks/useRouteInfo';

// import actions
import { updateFavorites } from 'js/store/user/User.actions';
import { toggleModalOverlay } from 'js/store/app/App.actions';

// import components
import SwiperCardTags from 'js/components/swiper/Swiper.tags';

// import icons
import IconFlip from '../icon/Icon.flip';
import IconHeart from '../icon/Icon.heart';
import IconShare from '../icon/Icon.share';
import IconAdd from '../icon/Icon.add';
import { trackEvent } from '../../utils/tracking';
import { getSkillTagTranslation } from '../../store/app/App.selectors';

const ActivityCardFront = React.memo(({ data, fullView = true }) => {
  const dispatch = useDispatch();
  const { location } = useRouteInfo();

  const locale = useSelector((state) => state.app.locale);
  const isOnline = useSelector(getOnlineStatus);
  const { playSFX } = useSFX();

  const siteText = {
    cardKid: useSelector((state) => getTranslationById(state, 'card_kid', 'Kid')),
    cardKids: useSelector((state) => getTranslationById(state, 'card_kids', 'Kids')),
    cardMins: useSelector((state) => getTranslationById(state, 'card_mins', 'Mins')),
    favIcon: useSelector((state) => getTranslationById(state, 'favorite_icon_alt_text', 'Add to favorites')),
    flipIcon: useSelector((state) => getTranslationById(state, 'flip_icon_alt_text', 'Flip the card')),
    addIcon: useSelector((state) => getTranslationById(state, 'add_icon_alt_text', 'Add to collection')),
    shareIcon: useSelector((state) => getTranslationById(state, 'share_icon_alt_text', 'Share the card')),
  };

  const frontData = useMemo(() => {
    return data?.front || {};
  }, [data]);

  const {
    title = '',
    largeImage = '',
    id,
    skillTags = [],
    finalPlayersTag = '',
    howManyMinutes = '',
    localisation = {},
  } = frontData;
  const { title: localizedTitle = title } = localisation || {};

  const attributes = useMemo(() => {
    const attrs = [];

    if (finalPlayersTag) {
      attrs.push(`${finalPlayersTag} ${finalPlayersTag === '1' ? siteText.cardKid : siteText.cardKids}`);
    }
    if (howManyMinutes) {
      attrs.push(`${howManyMinutes == '5 to 20' ? '5-20' : howManyMinutes} ${siteText.cardMins}`);
    }
    return attrs;
  }, [finalPlayersTag, howManyMinutes, siteText]);

  const favorited = useSelector((state) => state?.user?.favorites?.includes(id) || false, shallowEqual);
  const cardTitle = locale === 'es' ? localizedTitle : title;
  const cardSkillTags = [
    skillTags.map((tag) => {
      return useSelector((state) => getSkillTagTranslation(state, tag));
    }),
  ].flat();

  const addToFavorites = useCallback(() => {
    playSFX('button_tap');
    playSFX('hearting_a_card_feedback');
    trackEvent(favorited ? 'game_unfavorite_click' : 'game_favorite_click', {
      content_id: id,
      content_title: cardTitle,
    });
    dispatch(updateFavorites(id));
  }, [dispatch, id, cardTitle, favorited]);

  const shareCard = useCallback(() => {
    playSFX('button_tap');
    trackEvent('game_share_click', { content_id: id, content_title: cardTitle });
    dispatch(
      toggleModalOverlay({
        type: 'CARD',
        cardID: id,
        modalState: 'share',
      })
    );
  }, [dispatch, id, cardTitle]);

  const addToCollections = useCallback(() => {
    playSFX('button_tap');
    dispatch(
      toggleModalOverlay({
        type: 'COLLECTION',
        backgroundPath: location,
        cardID: id,
        modalState: 'add',
      })
    );
  }, []);

  return (
    <div className="card card--front">
      <button className="card__icon card__icon--favorite" onClick={addToFavorites} aria-label={siteText.favIcon}>
        <IconHeart active={favorited} />
      </button>

      {fullView && (
        <button className="card__icon card__icon--flip" aria-label={siteText.flipIcon}>
          <IconFlip />
        </button>
      )}

      <figure className="card__image-wrapper">
        <img
          src={isOnline ? largeImage : defaultCardImage}
          alt={cardTitle}
          className="card__image"
          onError={(e) => {
            e.currentTarget.onerror = null; // prevent infinite loop
            e.currentTarget.src = defaultCardImage;
          }}
        />
      </figure>

      <div className="card__content">
        <div className="card__details">
          <h3 className="card__title">{cardTitle}</h3>

          <ul className="card__attributes">
            {attributes.map((attr) => (
              <li key={`${id}_${attr}`} className="card__attribute">
                {attr}
              </li>
            ))}
          </ul>

          {fullView && cardSkillTags.length > 0 && (
            <SwiperCardTags
              slides={cardSkillTags.map((tag) => (
                <div key={`${id}_${tag}`} className="card__tag-wrapper" style={{ width: 'auto' }}>
                  <div
                    style={{
                      backgroundColor: '#8FCCF1',
                      color: '#003764',
                      fontSize: '14px',
                      borderRadius: '36px',
                      padding: '12px 16px',
                    }}
                  >
                    {tag}
                  </div>
                </div>
              ))}
            />
          )}
        </div>

        {fullView && (
          <div className="card__actions">
            <button aria-label={siteText.shareIcon} className="card__icon card__icon--share" onClick={shareCard}>
              <IconShare />
            </button>
            <button aria-label={siteText.addIcon} className="card__icon card__icon--add" onClick={addToCollections}>
              <IconAdd />
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

ActivityCardFront.displayName = 'ActivityCardFront';

export default ActivityCardFront;
